/**
 * @nurseEdu
 * 해당 코드는 현재 창이 모바일 크기인지 판단하기 위해 사용됩니다.
 * 단순 모바일 기기를 식별하기 위해서가 아닌, 데스크톱 환경에서도 창이 모바일 크기인지 아닌지 판단하기 위해 사용됩니다.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import debounce from '../lib/debounce';

const useIsMobileSize = () => {
  const [isMobile, setIsMobile] = useState<boolean | null>(null);

  // 1. mediaQueryList 생성 (SSR-safe)
  const mediaQueryList = useMemo(
    () => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)') : null),
    []
  );

  // 2. 실제 이벤트 리스너
  const handleMediaQueryChange = useCallback((event: MediaQueryListEvent) => {
    setIsMobile(event.matches);
  }, []);

  // 3. debounce로 감싸되, event를 받을 수 있게!
  const debouncedHandleMediaQueryChange = useMemo(
    () => debounce(handleMediaQueryChange, 100),
    [handleMediaQueryChange]
  );

  // 4. 초기 isMobile 값 설정
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsMobile(mediaQueryList?.matches ?? false);
    }
  }, [mediaQueryList]);

  // 5. 이벤트 리스너 등록 - 해제 시 cancel도 함께
  useEffect(() => {
    if (!mediaQueryList) return;

    mediaQueryList.addEventListener('change', debouncedHandleMediaQueryChange);

    return () => {
      mediaQueryList.removeEventListener('change', debouncedHandleMediaQueryChange);
      // cancel 메서드 안전 호출
      debouncedHandleMediaQueryChange.cancel();
    };
  }, [mediaQueryList, debouncedHandleMediaQueryChange]);

  return isMobile;
};

export default useIsMobileSize;
